import _ from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';

import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ResolveStateProps} from '../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {ContactOrAccountDetailsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {splitConversationsIntoRecentAndOlder} from '../../../../helpers/models/conversationHelpers';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {AppState} from '../../../../store/appState';
import {getConversations} from '../../../../store/data/dataSelectors';
import {fetchConversationsForAccountAsyncAction} from '../../../../store/panels/accounts/accountDetailsPanelActions';
import {ConversationList} from '../../../common/conversations/conversationList';
import {
  AccountDetailsHoverOptionsEnum,
  AccountDetailsOpenConversationSectionsEnum,
  buildAccountDetailsHoverEvent,
  buildAccountDetailsOpenConversationEvent,
} from '../../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from '../accountDetailsContext';

/*
 * Props.
 */

interface AccountDetailsConversationListContainerPublicProps {
  panelId: ContactOrAccountDetailsPanelId;
  domain?: string;
  accountId?: number;
  conversationIds: ReadonlyArray<number>;
  conversationLoadingStatus: AsyncStatusesEnum | undefined;
  hasMoreConversations: boolean | undefined;
}
type AccountDetailsConversationListContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountDetailsConversationListContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type AccountDetailsConversationListContainerProps = AccountDetailsConversationListContainerPublicProps &
  AccountDetailsConversationListContainerStateProps &
  AccountDetailsConversationListContainerDispatchProps;

/*
 * Selectors.
 */

function getConversationIds(_state: AppState, props: AccountDetailsConversationListContainerPublicProps) {
  return props.conversationIds;
}

function makeGetConversations() {
  return createSelector([getConversations, getConversationIds], (conversations, conversationIds) =>
    _(conversationIds)
      .uniq()
      .map((conversationId) => conversations[conversationId])
      .compact()
      .value(),
  );
}

/*
 * Component.
 */

const AccountDetailsConversationListContainerComponent: FC<AccountDetailsConversationListContainerProps> = (
  props,
) => {
  const {panelId, conversations, conversationLoadingStatus, hasMoreConversations, fetchConversations} = props;
  const {onTrackEvent} = useAccountDetailsContext();

  const onConversationClick = (conversation: ConversationModel) => {
    const [recentConversations] = splitConversationsIntoRecentAndOlder(conversations);
    const section = computeSectionForAnalytics(
      conversation,
      recentConversations,
      recentConversations.length > 0,
    );
    onTrackEvent(buildAccountDetailsOpenConversationEvent(section));
  };

  const onConversationHoverTrack = () => {
    onTrackEvent(buildAccountDetailsHoverEvent(AccountDetailsHoverOptionsEnum.CONVERSATION));
  };

  return (
    <ConversationList
      panelId={panelId}
      conversations={conversations}
      conversationLoadingStatus={conversationLoadingStatus}
      hasMoreConversations={hasMoreConversations}
      conversationIdsToHighlight={[]}
      onConversationClick={onConversationClick}
      fetchConversations={fetchConversations}
      onConversationHoverTrack={onConversationHoverTrack}
    />
  );
};

/*
 * Helpers.
 */

function computeSectionForAnalytics(
  conversation: ConversationModel,
  recentConversations: ReadonlyArray<ConversationModel>,
  isShowingSubtitle: boolean,
) {
  if (!isShowingSubtitle) {
    return AccountDetailsOpenConversationSectionsEnum.NONE;
  }

  if (recentConversations.includes(conversation)) {
    return AccountDetailsOpenConversationSectionsEnum.LAST_7D;
  }

  return AccountDetailsOpenConversationSectionsEnum.OLDER;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountDetailsConversationListContainerPublicProps>()(() => ({
  conversations: makeGetConversations(),
}));

const mapDispatchToProps = makeMapDispatchToProps<AccountDetailsConversationListContainerPublicProps>()(
  ({panelId, domain, accountId}) => ({
    fetchConversations: () => fetchConversationsForAccountAsyncAction(panelId, accountId, domain),
  }),
);

export const AccountDetailsConversationListContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountDetailsConversationListContainerComponent);
